import moment from "moment";

import { CNAB_PAYER } from "config";
import { api } from "services";

import { generateCnab240, CnabCompany, CnabPayment } from "./cnab240";
import { toast } from "./toast";

export interface CnabExportItem {
  quoteId: number;
  professionalId?: number | null;
  professionalName: string;
  value: number; // valor do repasse (compensationValue)
}

export interface CnabDayGroup {
  companyId: number;
  companyName: string;
  items: CnabExportItem[];
}

interface ProfessionalInfo {
  cpf: string;
  email: string;
  phone: string;
}

// CPF/contato dos profissionais das instituições (chave PIX dos favorecidos)
async function buildProfessionalMap(
  companyIds: number[]
): Promise<Record<number, ProfessionalInfo>> {
  const profMap: Record<number, ProfessionalInfo> = {};

  await Promise.all(
    companyIds.map(async (companyId) => {
      let page = 1;
      while (true) {
        const res = await api.get("/Professional/search", {
          params: { page, size: 200, companyId, active: true },
          timeout: 30000,
        });
        const profs: {
          id: number;
          cpf?: string;
          email?: string;
          dddMobilePhone?: string;
          mobilePhone?: string;
        }[] = Array.isArray(res.data) ? res.data : [];
        for (const p of profs) {
          profMap[p.id] = {
            cpf: p.cpf ?? "",
            email: p.email ?? "",
            phone: `${p.dddMobilePhone ?? ""}${p.mobilePhone ?? ""}`,
          };
        }
        if (profs.length < 200) break;
        page++;
      }
    })
  );

  return profMap;
}

interface DownloadCnabDayOptions {
  creditDate: string; // YYYY-MM-DD — data de crédito no banco (dia do repasse)
  groups: CnabDayGroup[];
}

// Gera e baixa UM arquivo de remessa CNAB 240 com todos os repasses do dia.
// Conta pagadora = DocttorPay (CNAB_PAYER, configurada via .env);
// favorecidos = profissionais de cada orçamento (chave PIX por CPF).
export async function downloadCnabForDay(
  opts: DownloadCnabDayOptions
): Promise<boolean> {
  const { creditDate, groups } = opts;

  const items = groups.flatMap((g) => g.items).filter((i) => i.value > 0);
  if (items.length === 0) {
    toast.fire({
      icon: "warning",
      title: "Nenhum repasse com valor neste dia.",
    });
    return false;
  }

  if (!CNAB_PAYER.cnpj || !CNAB_PAYER.agency || !CNAB_PAYER.account) {
    toast.fire({
      icon: "warning",
      title:
        "Conta pagadora não configurada (REACT_APP_CNAB_PAYER_*). Arquivo gerado com o campo em branco — preencha o .env para uso real.",
      timer: 8000,
    });
  }

  const payer: CnabCompany = {
    cnpj: CNAB_PAYER.cnpj,
    name: CNAB_PAYER.name,
    agency: CNAB_PAYER.agency,
    agencyDv: CNAB_PAYER.agencyDv,
    account: CNAB_PAYER.account,
    accountDv: CNAB_PAYER.accountDv,
    agencyAccountDv: " ",
    conventionCode: CNAB_PAYER.conventionCode,
  };

  const profMap = await buildProfessionalMap(
    groups.filter((g) => g.companyId > 0).map((g) => g.companyId)
  );

  const payments: CnabPayment[] = items.map((item) => {
    const profInfo = (item.professionalId && profMap[item.professionalId]) || {
      cpf: "",
      email: "",
      phone: "",
    };
    return {
      quoteId: item.quoteId,
      professional: {
        name: item.professionalName,
        cpf: profInfo.cpf,
        email: profInfo.email,
        phone: profInfo.phone,
      },
      value: item.value,
      paymentDate: creditDate,
    };
  });

  const content = generateCnab240(payments, payer);
  const blob = new Blob([content], { type: "text/plain;charset=ascii" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cnab240-repasse-${moment(creditDate).format("YYYYMMDD")}.rem`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  const total = items.reduce((sum, i) => sum + i.value, 0);
  toast.fire({
    icon: "success",
    title: `CNAB 240 do dia ${moment(creditDate).format(
      "DD/MM/YYYY"
    )}: ${payments.length} repasse(s) — ${total.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    })}.`,
  });
  return true;
}
